/**
 * Central place for handling an expired / invalid auth token.
 *
 * When any API call comes back 401 (token expired/invalid) or 403 (no longer authorized), we clear
 * the stored session and broadcast a global `auth:unauthorized` event. `AuthProvider` listens for it
 * and logs the user out, which flips `isAuthenticated` to false so `ProtectedRoute` redirects to
 * `/login`. This fixes the "stuck inside the app with nothing loading" state that happened when the
 * token expired but was still present in storage (so the route guard thought the user was signed in).
 *
 * Both the generated API client (via apiConfig middleware) and any raw `fetch` calls (via authFetch)
 * funnel through here, so there is a single logout trigger regardless of how the request was made.
 */

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

/** True for HTTP statuses that mean "your session is no longer valid". */
export function isAuthFailure(status: number): boolean {
  return status === 401 || status === 403;
}

/** Clear every auth key from both stores (localStorage = remembered, sessionStorage = session-only). */
export function clearStoredAuth(): void {
  ['token', 'role', 'email', 'accessToken'].forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
}

/**
 * Handle an auth failure once: clear storage and notify listeners. Guarded so a burst of concurrent
 * 401s (e.g. a dashboard firing several queries at once) only triggers a single logout/redirect.
 */
let handling = false;
export function handleAuthFailure(): void {
  if (handling) return;
  handling = true;
  clearStoredAuth();
  window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
  // Release the guard on the next tick so future (post-login) failures are handled again.
  setTimeout(() => {
    handling = false;
  }, 0);
}

/**
 * Drop-in replacement for `fetch` that funnels auth failures through `handleAuthFailure`. Use this
 * for hand-written requests that bypass the generated client's middleware.
 */
export async function authFetch(...args: Parameters<typeof fetch>): Promise<Response> {
  const res = await fetch(...args);
  if (isAuthFailure(res.status)) {
    handleAuthFailure();
  }
  return res;
}
